/* Agendamientos - vista del usuario (TD-11766).
 * Pestañas: agendas disponibles (según el público de cada una) y "Mis citas"
 * (próximas e historial, con cancelar, reprogramar, enlace virtual y encuesta).
 */
import {
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonAlert,
  useIonToast,
} from "@ionic/react";
import { CircularProgress, TextField } from "@material-ui/core";
import { calendarOutline, downloadOutline, locationOutline, logoGoogle, timeOutline, videocamOutline } from "ionicons/icons";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Header from "src/components/Header";
import AgendaAPI, { avisarCambioAgenda, MiReserva, TemaDisponible } from "src/services/agenda";
import {
  CATEGORIAS,
  colorCategoria,
  ESTADO_RESERVA,
  ETIQUETA_MODALIDAD,
  eventoDeCita,
  formatFechaLarga,
  hora,
} from "src/utils/agenda";
import { descargarIcs, urlGoogleCalendar } from "src/utils/calendario";
import { mensajeError } from "src/utils/errores";
import { formatFechaCorta } from "src/utils/fechas";
import "./Agenda.css";

type Tab = "DISPONIBLES" | "MIS";

const AgendaUsuario: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [present] = useIonToast();
  const [presentAlert] = useIonAlert();

  const [tab, setTab] = useState<Tab>(new URLSearchParams(location.search).get("tab") === "mis" ? "MIS" : "DISPONIBLES");
  const [cargando, setCargando] = useState(true);
  const [temas, setTemas] = useState<TemaDisponible[]>([]);
  const [reservas, setReservas] = useState<MiReserva[]>([]);

  const [encuesta, setEncuesta] = useState<MiReserva | null>(null);
  const [puntaje, setPuntaje] = useState(0);
  const [comentario, setComentario] = useState("");
  const [enviandoEncuesta, setEnviandoEncuesta] = useState(false);

  const cargar = async () => {
    try {
      const [inicio, mias] = await Promise.all([AgendaAPI.inicio(), AgendaAPI.misReservas()]);
      setTemas(inicio.disponibles);
      setReservas(mias);
    } catch (err: any) {
      setTemas([]);
      setReservas([]);
      present({ message: mensajeError(err, "No se pudo cargar la agenda"), duration: 3500, color: "danger" });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Si llega con ?tab=mis (desde el Home o tras reservar) se respeta.
  useEffect(() => {
    if (new URLSearchParams(location.search).get("tab") === "mis") setTab("MIS");
  }, [location.search]);

  const proximas = useMemo(() => {
    const ahora = Date.now();
    return reservas.filter(
      (r) => ["PENDIENTE", "CONFIRMADA", "ESPERA"].includes(r.estado) && new Date(r.fecha_fin.replace(" ", "T")).getTime() > ahora
    );
  }, [reservas]);
  const historial = useMemo(() => reservas.filter((r) => !proximas.includes(r)), [reservas, proximas]);

  const cancelar = (r: MiReserva) =>
    presentAlert({
      header: "Cancelar cita",
      message: `${r.titulo} · ${formatFechaLarga(r.fecha_inicio)}`,
      inputs: [{ name: "motivo", type: "textarea", placeholder: "Motivo (opcional)" }],
      buttons: [
        "Volver",
        {
          text: "Cancelar cita",
          role: "destructive",
          handler: async (data: { motivo?: string }) => {
            try {
              const res = await AgendaAPI.cancelar(r.id, (data?.motivo || "").trim());
              present({ message: res.info, duration: 2500, color: "success" });
              avisarCambioAgenda();
              await cargar();
            } catch (err: any) {
              present({ message: mensajeError(err, "No se pudo cancelar"), duration: 4000, color: "danger" });
            }
          },
        },
      ],
    });

  const enviarEncuesta = async () => {
    if (!encuesta || !puntaje) return;
    setEnviandoEncuesta(true);
    try {
      const res = await AgendaAPI.encuesta(encuesta.id, puntaje, comentario.trim());
      present({ message: res.info, duration: 2500, color: "success" });
      setEncuesta(null);
      avisarCambioAgenda();
      await cargar();
    } catch (err: any) {
      present({ message: mensajeError(err, "No se pudo enviar"), duration: 3500, color: "danger" });
    } finally {
      setEnviandoEncuesta(false);
    }
  };

  /** iPhone, Outlook y demás: archivo .ics (en el celular abre "Compartir"). */
  const agregarAOtroCalendario = async (r: MiReserva) => {
    try {
      await descargarIcs(eventoDeCita(r));
    } catch {
      present({ message: "No se pudo generar el evento del calendario", duration: 3000, color: "danger" });
    }
  };

  const tarjetaReserva = (r: MiReserva) => (
    <div key={r.id} className="agenda-card acento" style={{ ["--agenda-color" as any]: colorCategoria(r.categoria) }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <div>
          <span className="agenda-etiqueta">{CATEGORIAS[r.categoria]?.etiqueta}</span>
          <p className="agenda-card-titulo">{r.titulo}</p>
        </div>
        <IonBadge color={ESTADO_RESERVA[r.estado].color} style={{ alignSelf: "flex-start" }}>
          {ESTADO_RESERVA[r.estado].etiqueta}
        </IonBadge>
      </div>
      <div className="agenda-meta">
        <span>
          <IonIcon icon={calendarOutline} /> {formatFechaLarga(r.fecha_inicio)} a {hora(r.fecha_fin)}
        </span>
        {r.responsable_nombre && <span>con {r.responsable_nombre}</span>}
        <span>{ETIQUETA_MODALIDAD[r.modalidad]}</span>
        {r.lugar && r.modalidad !== "VIRTUAL" && (
          <span>
            <IonIcon icon={locationOutline} /> {r.lugar}
          </span>
        )}
      </div>
      {r.asistencia && (
        <div className="agenda-meta">
          <span style={{ color: r.asistencia === "PRESENTE" ? "#047857" : "#b91c1c" }}>
            {r.asistencia === "PRESENTE" ? "Asististe" : "Registrada como inasistencia"}
          </span>
        </div>
      )}
      {r.motivo && (
        <div className="agenda-meta">
          <span>Motivo: {r.motivo}</span>
        </div>
      )}
      {r.estado === "ESPERA" && (
        <div className="agenda-aviso info" style={{ marginTop: 10, marginBottom: 0 }}>
          Estás en lista de espera. Si se libera un lugar lo tomás automáticamente y te avisamos.
        </div>
      )}

      {r.estado === "CONFIRMADA" && new Date(r.fecha_fin.replace(" ", "T")).getTime() > Date.now() && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10, alignItems: "center" }}>
          <span className="agenda-card-sub" style={{ margin: 0 }}>Agregar a mi calendario:</span>
          <IonButton size="small" fill="outline" href={urlGoogleCalendar(eventoDeCita(r))} target="_blank" rel="noopener noreferrer">
            <IonIcon slot="start" icon={logoGoogle} /> Google Calendar
          </IonButton>
          <IonButton size="small" fill="outline" color="medium" onClick={() => agregarAOtroCalendario(r)}>
            <IonIcon slot="start" icon={downloadOutline} /> Otro calendario
          </IonButton>
        </div>
      )}

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
        {r.link_virtual && (
          <IonButton size="small" href={r.link_virtual} target="_blank" rel="noopener noreferrer">
            <IonIcon slot="start" icon={videocamOutline} /> Unirme
          </IonButton>
        )}
        {!r.link_virtual && r.link_desde && (
          <span className="agenda-card-sub" style={{ margin: 0, alignSelf: "center" }}>
            <IonIcon icon={videocamOutline} /> El enlace aparece desde {hora(r.link_desde)} del día de la cita
          </span>
        )}
        {r.puede_reprogramar && (
          <IonButton size="small" fill="outline" onClick={() => history.push(`/agenda/tema/${r.tema_id}?reprogramar=${r.id}`)}>
            Reprogramar
          </IonButton>
        )}
        {r.puede_cancelar && (
          <IonButton size="small" fill="clear" color="danger" onClick={() => cancelar(r)}>
            Cancelar
          </IonButton>
        )}
        {r.encuesta_pendiente && (
          <IonButton size="small" color="warning" onClick={() => { setEncuesta(r); setPuntaje(0); setComentario(""); }}>
            Calificar
          </IonButton>
        )}
      </div>
      {!r.puede_cancelar && r.limite_cancelacion && r.estado !== "ESPERA" && (
        <p className="agenda-card-sub" style={{ margin: "6px 0 0" }}>
          Ya pasó el plazo para cancelar o reprogramar desde la app. Si no podés asistir, avisá al responsable.
        </p>
      )}
    </div>
  );

  return (
    <IonPage>
      <Header titulo="Agendamientos" />
      <IonContent id="agenda-usuario">
        <div className="agenda-page campos-mui">
          <div className="agenda-chips">
            <button type="button" className={`agenda-chip${tab === "DISPONIBLES" ? " activo" : ""}`} onClick={() => setTab("DISPONIBLES")}>
              Agendas disponibles ({temas.length})
            </button>
            <button type="button" className={`agenda-chip${tab === "MIS" ? " activo" : ""}`} onClick={() => setTab("MIS")}>
              Mis citas ({proximas.length})
            </button>
          </div>

          {cargando ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
              <CircularProgress />
            </div>
          ) : tab === "DISPONIBLES" ? (
            temas.length === 0 ? (
              <div className="agenda-vacio">
                <IonIcon icon={calendarOutline} />
                No hay agendas disponibles para vos en este momento.
              </div>
            ) : (
              temas.map((t) => (
                <div key={t.id} className="agenda-card acento" role="button" style={{ cursor: "pointer", ["--agenda-color" as any]: colorCategoria(t.categoria) }}
                  onClick={() => history.push(`/agenda/tema/${t.id}`)}>
                  <span className="agenda-etiqueta">{CATEGORIAS[t.categoria]?.etiqueta}</span>
                  <p className="agenda-card-titulo">{t.titulo}</p>
                  {t.descripcion && (
                    <p className="agenda-card-sub" style={{ margin: 0, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {t.descripcion}
                    </p>
                  )}
                  <div className="agenda-meta">
                    <span>
                      <IonIcon icon={timeOutline} /> {t.duracion_minutos} min
                    </span>
                    <span>{ETIQUETA_MODALIDAD[t.modalidad]}</span>
                    {t.responsables.length > 0 && <span>{t.responsables.map((r) => r.nombre).join(", ")}</span>}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
                    <span className="agenda-card-sub" style={{ margin: 0 }}>
                      {!t.inscripcion_abierta
                        ? `Inscripciones desde ${formatFechaCorta(t.apertura_inscripcion)}`
                        : t.mis_activas > 0
                        ? `Tenés ${t.mis_activas} cita(s) reservada(s)`
                        : t.requiere_aprobacion
                        ? "Requiere aprobación"
                        : ""}
                    </span>
                    <IonButton size="small">{t.inscripcion_abierta ? "Ver turnos" : "Ver"}</IonButton>
                  </div>
                </div>
              ))
            )
          ) : (
            <>
              {proximas.length === 0 ? (
                <div className="agenda-vacio">
                  <IonIcon icon={calendarOutline} />
                  No tenés citas próximas.
                </div>
              ) : (
                proximas.map(tarjetaReserva)
              )}
              {historial.length > 0 && (
                <>
                  <div className="agenda-dia-titulo">Historial</div>
                  {historial.map(tarjetaReserva)}
                </>
              )}
            </>
          )}
        </div>

        <IonModal isOpen={Boolean(encuesta)} onDidDismiss={() => setEncuesta(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>¿Cómo fue tu cita?</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setEncuesta(null)}>Más tarde</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            {encuesta && (
              <div className="agenda-page campos-mui">
                <p className="agenda-card-titulo">{encuesta.titulo}</p>
                <p className="agenda-card-sub">{formatFechaLarga(encuesta.fecha_inicio)}</p>
                <div className="agenda-estrellas">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" className={n <= puntaje ? "activa" : ""} onClick={() => setPuntaje(n)} aria-label={`${n} de 5`}>
                      ★
                    </button>
                  ))}
                </div>
                <TextField label="Comentario (opcional)" variant="outlined" fullWidth multiline minRows={3} value={comentario}
                  inputProps={{ maxLength: 1000 }} onChange={(e) => setComentario(e.target.value)} />
                <IonButton expand="block" style={{ marginTop: 16 }} disabled={!puntaje || enviandoEncuesta} onClick={enviarEncuesta}>
                  {enviandoEncuesta ? "Enviando..." : "Enviar"}
                </IonButton>
              </div>
            )}
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default AgendaUsuario;
